import { VuexModule, Module, Action, Mutation } from 'vuex-module-decorators';
import Vue from 'vue';
import Photo from '@/model/Photo';
import api from '@/service/api';

const { $vToastify: notifier } = new Vue() as any;
const sessionPhotoLoads = new Map<string, Promise<void>>();
const orderedPhotoDeleteMessage = {
    title: 'Nie można usunąć.',
    body: 'Sesja ma kupione zdjęcia.',
};
const deletePhotoErrorMessage = { title: 'Błąd usuwania plików' };

export type photoQueueStatus = 'waiting'|'progress'|'finished';
export type Conflict = {
    conflictId: string,
    conflictType: 'FILENAME'|'HASH',
    photo: {
        filename: string;
        file: File;
    },
    smallThumb: string,
    largeThumb: string,
    conflicted: Photo,
    sessionId: string,
    resolve: (id?: string) => void
};

@Module({
    name: 'photos',
})
export default class PhotosModule extends VuexModule {
    public sessionPhotoIds: { [ id: string ]: string[] } = {};
    public remotePhotos: { [id: string]: Photo } = {};
    public existingPhotoIds: string[] = [];
    public photosLoaded: Array<string> = [];

    @Mutation
    public markSessionPhotosLoaded(sessionId: string) {
        if (!this.photosLoaded.includes(sessionId)) {
            this.photosLoaded = [...this.photosLoaded, sessionId];
        }
    }

    @Mutation
    public addRemotePhoto(photo: Photo) {
        if (this.existingPhotoIds.includes(photo.id)) {
            const randomString = (Math.random() + 1).toString(36).substring(7);
            photo = {
                ...photo,
                smallThumbUrl: `${photo.smallThumbUrl}?f=${randomString}`,
                largeThumbUrl: `${photo.largeThumbUrl}?f=${randomString}`,
            };
        }
        Vue.set(
            this.remotePhotos,
            photo.id,
            photo,
        );
        if (!this.existingPhotoIds.includes(photo.id)) {
            this.existingPhotoIds.push(photo.id);
        }
    }

    @Mutation
    public removePhoto(photoId: string) {
        Object.keys(this.sessionPhotoIds)
            .forEach((sessionId) => {
                if (this.sessionPhotoIds[sessionId].includes(photoId)) {
                    Vue.set(this.sessionPhotoIds, sessionId, this.sessionPhotoIds[sessionId].filter(
                        (sessionPhotoId) => sessionPhotoId !== photoId,
                    ));
                }
            });

        if (Object.keys(this.remotePhotos).includes(photoId)) {
            Vue.delete(this.remotePhotos, photoId);
            this.existingPhotoIds = this.existingPhotoIds.filter(id => id !== photoId);
        }
    }

    @Mutation
    public addRemoteSessionPhotos({ photos, sessionId }: { photos: Photo[], sessionId: string }) {
        const filteredPhotos = photos
            .filter(({ id }) => !this.existingPhotoIds.includes(id));
            
        filteredPhotos.forEach((photo) => {
            Vue.set(this.remotePhotos, photo.id, photo);
            this.existingPhotoIds.push(photo.id);
        });
        Vue.set(
            this.sessionPhotoIds,
            sessionId, [ ...(this.sessionPhotoIds[sessionId] || []), ...filteredPhotos.map((photo) => photo.id) ],
        );
    }

    @Mutation
    public addSessionPhotos({ sessionId, photoIds }: { sessionId: string, photoIds: string[] }) {
        Vue.set(
            this.sessionPhotoIds,
            sessionId, [ ...(this.sessionPhotoIds[sessionId] || []), ...photoIds ],
        );
    }

    @Mutation
    public updateRemotePhoto(
        { photoId, params }: { photoId: string, params: Partial<Photo>},
    ) {
        this.remotePhotos[photoId] = { ...this.remotePhotos[photoId], ...params };
    }

    @Mutation
    public replaceSessionSaleOptions(photos: Photo[]) {
        photos.forEach(({ id, surcharge, saleMode }) => {
            if (this.remotePhotos[id]) {
                Vue.set(this.remotePhotos, id, { ...this.remotePhotos[id], surcharge, saleMode });
            }
        });
    }

    @Action({ rawError: true })
    public async refreshSessionSaleOptions(sessionId: string) {
        this.replaceSessionSaleOptions(await api.loadSessionPhotos(sessionId));
    }

    @Action({
        rawError: true,
    })
    public updatePhoto(
        arg: { photoId: string, params: Partial<Photo>},
    ) {
        const { photoId } = arg;
        if (this.remotePhotos[photoId]) {
            this.updateRemotePhoto(arg);
        }
    }

    @Action({
        rawError: true,
        commit: 'addRemoteSessionPhotos',
    })
    public async loadSessionPhotos(sessionId: string) {
        const photos = await api.loadSessionPhotos(sessionId);

        return { photos, sessionId };
    }

    @Action({
        rawError: true
    })
    public async loadSessionPhotosIfNotLoaded(sessionId: string) {
        if (this.photosLoaded.includes(sessionId)) {
            return;
        }

        const existingLoad = sessionPhotoLoads.get(sessionId);
        if (existingLoad) {
            await existingLoad;
            return;
        }

        const load = (async () => {
            await this.loadSessionPhotos(sessionId);
            this.markSessionPhotosLoaded(sessionId);
        })();
        sessionPhotoLoads.set(sessionId, load);

        try {
            await load;
        } finally {
            sessionPhotoLoads.delete(sessionId);
        }
    }

    @Action({
        rawError: true,
    })
    public async deletePhoto(photoId: string) {
        if ((this.remotePhotos[photoId]?.orderedBy || []).length > 0) {
            notifier.error(orderedPhotoDeleteMessage);
            return false;
        }

        try {
            await api.deletePhoto(photoId);
        } catch (error) {
            notifier.error(deletePhotoErrorMessage);
            return false;
        }
        this.removePhoto(photoId);
        setTimeout(() => this.context.dispatch('refreshUsedStorageBytes'), 10000);
        return true;
    }

    @Action({ rawError: true })
    public async updateSaleOptions({ photoId, surcharge, saleMode }: { photoId: string; surcharge: number|null; saleMode: 'all'|'noDigital'|'digitalOnly' }) {
        await api.updatePhotoSaleOptions(photoId, { surcharge, saleMode });
        this.updateRemotePhoto({ photoId, params: { surcharge, saleMode } });
    }

    @Action({ rawError: true })
    public async updateMultipleSaleOptions({ photoIds, surcharge, saleMode }: { photoIds: string[]; surcharge: number|null; saleMode: 'all'|'noDigital'|'digitalOnly' }) {
        await api.updatePhotosSaleOptions(photoIds, { surcharge, saleMode });
        photoIds.forEach((photoId) => this.updateRemotePhoto({ photoId, params: { surcharge, saleMode } }));
    }

    @Action({
        rawError: true,
    })
    public async deletePhotos(photoIds: string[]) {
        if (photoIds.length === 0) return true;
        if (photoIds.some((photoId) => (this.remotePhotos[photoId]?.orderedBy || []).length > 0)) {
            notifier.error(orderedPhotoDeleteMessage);
            return false;
        }

        try {
            await api.deletePhotos(photoIds);
        } catch (error) {
            notifier.error(deletePhotoErrorMessage);
            return false;
        }
        photoIds.forEach((photoId) => this.removePhoto(photoId));
        setTimeout(() => this.context.dispatch('refreshUsedStorageBytes'), 10000);
        return true;
    }

    @Action({
        rawError: true
    })
    public async refreshPhoto(photoId: string) {
        const currentTimestamp = Math.floor(Date.now());
        const smallThumbUrl = `${this.remotePhotos[photoId].smallThumbUrl}?ts=${currentTimestamp}`;
        const largeThumbUrl = `${this.remotePhotos[photoId].largeThumbUrl}?ts=${currentTimestamp}`;
        this.updateRemotePhoto({
            photoId,
            params: {
                smallThumbUrl,
                largeThumbUrl,
            }
        })
    }

    @Action({
        rawError: true
    })
    public async recommend(photoId: string) {
        this.updatePhoto({
            photoId,
            params: {
                recommended: true,
            }
        })
        await api.recommendPhoto(photoId);
    }

    @Action({
        rawError: true
    })
    public async finalize(photoId: string) {
        await api.finalizePhoto(photoId);
        this.updatePhoto({
            photoId,
            params: {
                finalized: true,
            }
        })
        notifier.success({
            title: 'Sfinalizowano',
            body: `Zdjęcie ${this.remotePhotos[photoId].filename} zostało sfinalizowane.`,
        });
    }

    @Action({
        rawError: true
    })
    public async recommendMultiple(photoIds: string[]) {
        photoIds.forEach(photoId => this.updatePhoto({
            photoId,
            params: {
                recommended: true,
            }
        }));
        await api.recommendMultiplePhotos(photoIds);
    }

    @Action({
        rawError: true
    })
    public async unrecommendMultiple(photoIds: string[]) {
        photoIds.forEach(photoId => this.updatePhoto({
            photoId,
            params: {
                recommended: false,
            }
        }));
        await api.unrecommendMultiplePhotos(photoIds);
    }

    @Action({
        rawError: true
    })
    public async unrecommend(photoId: string) {
        this.updatePhoto({
            photoId,
            params: {
                recommended: false,
            }
        })
        await api.unrecommendPhoto(photoId);
    }

    @Action({
        rawError: true
    })
    public async unfinalize(photoId: string) {
        await api.unfinalizePhoto(photoId);
        this.updatePhoto({
            photoId,
            params: {
                finalized: false,
            }
        })
        notifier.success({
            title: 'Cofnięto finalizację',
            body: `Zdjęcie ${this.remotePhotos[photoId].filename} zostało cofnięte z finalizacji.`,
        });
    }

    @Action({
        rawError: true
    })
    public async markPhotosOrdered(orderData: { sessionId: string; photoIds: string[]; clientName: string; sessionName: string; }) {
        notifier.success({
            title: 'Złożono zamówienie',
            body: `Zamówienie dla sesji ${orderData.sessionName} zostało złożone przez ${orderData.clientName}.`,
        });
        orderData.photoIds.filter(photoId => this.remotePhotos[photoId]).forEach(photoId => this.updatePhoto({
            photoId,
            params: {
                orderedBy: [ ...(this.remotePhotos[photoId].orderedBy || []), orderData.clientName ],
            }
        }));
    }

    get sessionPhotos(): { [sessionId: string]: Photo[] } {
        const allPhotoIds = Object.keys(this.remotePhotos);

        return Object.keys(this.sessionPhotoIds).reduce((prev, curr) => ({
            ...prev,
            [curr]: this.sessionPhotoIds[curr]
                .filter((photoId) => allPhotoIds.includes(photoId))
                .map((photoId) => this.remotePhotos[photoId]),
        }), {});
    }
}
